import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

// Cart Timer - Fixed initialization
function initCartTimer() {
  const timerElement = document.querySelector('.cart-timer');
  if (!timerElement) return;
  
  const minutes = parseInt(timerElement.dataset.timerMinutes) || 5;
  const clearCart = timerElement.dataset.clearCart === 'true';
  const countdownSpan = timerElement.querySelector('.cart-timer__countdown');
  
  if (!countdownSpan) return;
  
  const storageKey = 'cart_timer_expiry';
  let timeLeft = minutes * 60;
  
  try {
    const savedExpiry = localStorage.getItem(storageKey);
    if (savedExpiry) {
      const now = Date.now();
      const expiry = parseInt(savedExpiry);
      
      if (now < expiry) {
        timeLeft = Math.floor((expiry - now) / 1000);
      } else {
        localStorage.removeItem(storageKey);
        if (clearCart) {
          fetch('/cart/clear.js', { method: 'POST' })
            .then(() => location.reload())
            .catch(err => console.error('Clear cart error:', err));
          return;
        }
      }
    } else {
      const expiryTime = Date.now() + (timeLeft * 1000);
      localStorage.setItem(storageKey, String(expiryTime));
    }
  } catch (e) {
    console.error('localStorage error:', e);
  }
  
  function updateTimer() {
    if (timeLeft <= 0) {
      try {
        localStorage.removeItem(storageKey);
      } catch (e) {}
      if (clearCart) {
        fetch('/cart/clear.js', { method: 'POST' })
          .then(() => location.reload())
          .catch(err => console.error('Clear cart error:', err));
      }
      return;
    }
    
    const mins = Math.floor(timeLeft / 60);
    const secs = timeLeft % 60;
    const timeString = String(mins).padStart(2, '0') + ':' + String(secs).padStart(2, '0');
    
    if (countdownSpan) {
      countdownSpan.textContent = timeString;
    }
    
    timeLeft--;
    setTimeout(updateTimer, 1000);
  }
  
  updateTimer();
}

// Shipping Protection Toggle - Keep as separate line item but hide from cart products
document.addEventListener('change', function(e) {
  if (e.target.classList.contains('cart-protection__input')) {
    const input = e.target;
    const isChecked = input.checked;
    const variantId = input.dataset.variantId;
    const lineId = input.dataset.lineId;
    
    input.disabled = true;
    
    if (isChecked) {
      // Add protection to cart
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          id: variantId, 
          quantity: 1,
          properties: {
            '_shipping_protection': 'true'
          }
        })
      })
      .then(response => response.json())
      .then(data => {
        input.dataset.lineKey = data.key;
        input.dataset.lineId = data.id;
        input.disabled = false;
        updateCartTotals();
      })
      .catch(err => {
        console.error('Add protection error:', err);
        input.checked = false;
        input.disabled = false;
      });
    } else {
      // Remove protection from cart
      if (lineId) {
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineId, quantity: 0 })
        })
        .then(() => {
          input.dataset.lineKey = '';
          input.dataset.lineId = '';
          input.disabled = false;
          updateCartTotals();
        })
        .catch(err => {
          console.error('Remove protection error:', err);
          input.checked = true;
          input.disabled = false;
        });
      } else {
        input.disabled = false;
      }
    }
  }
});

function updateCartTotals() {
  fetch('/cart.js')
    .then(res => res.json())
    .then(cart => {
      const totalPrice = cart.total_price;
      const formattedPrice = window.Shopify && window.Shopify.formatMoney 
        ? Shopify.formatMoney(totalPrice) 
        : (totalPrice / 100).toFixed(2);
      
      // Update all cart total displays
      document.querySelectorAll('[data-cart-total]').forEach(el => {
        el.textContent = formattedPrice;
      });
      
      // Update free shipping bar and status
      updateFreeShippingStatus(cart.total_price);
      
      // Update protection status
      updateProtectionStatus(cart);
    })
    .catch(err => console.error('Update totals error:', err));
}

function updateFreeShippingStatus(cartTotal) {
  const freeShippingBar = document.querySelector('.free-shipping-bar');
  if (!freeShippingBar) return;
  
  const threshold = 15500; // €155 in cents
  
  const remaining = threshold - cartTotal;
  const percentage = Math.min((cartTotal / threshold) * 100, 100);
  
  // Update progress bar
  const fill = freeShippingBar.querySelector('.free-shipping-bar__fill');
  const icon = freeShippingBar.querySelector('.free-shipping-bar__icon-wrapper');
  if (fill) fill.style.width = percentage + '%';
  if (icon) icon.style.left = percentage + '%';
  
  // Update text
  const textElement = freeShippingBar.querySelector('.free-shipping-bar__text');
  if (textElement) {
    if (remaining > 0) {
      const remainingFormatted = window.Shopify && window.Shopify.formatMoney 
        ? Shopify.formatMoney(remaining) 
        : '€' + (remaining / 100).toFixed(2);
      textElement.innerHTML = `🎉 Add <strong>${remainingFormatted}</strong> to unlock <strong>Free shipping!</strong>`;
      textElement.classList.remove('free-shipping-bar__text--unlocked');
    } else {
      textElement.innerHTML = '🎉 <strong>Free shipping unlocked!</strong>';
      textElement.classList.add('free-shipping-bar__text--unlocked');
    }
  }
  
  // Update status text under subtotal with checkmark
  const statusContainer = document.querySelector('.cart-subtotal-section');
  if (statusContainer) {
    let statusText = statusContainer.querySelector('.cart-status-text:not(.cart-status-text--protected)');
    if (remaining <= 0) {
      if (!statusText) {
        statusText = document.createElement('div');
        statusText.className = 'cart-status-text';
        statusText.innerHTML = `
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M11.667 3.5L5.25 9.917 2.333 7" stroke="#10B981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          FREE Shipping applied
        `;
        const subtotal = statusContainer.querySelector('.cart-subtotal');
        if (subtotal) {
          subtotal.insertAdjacentElement('afterend', statusText);
        }
      } else {
        statusText.innerHTML = `
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M11.667 3.5L5.25 9.917 2.333 7" stroke="#10B981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          FREE Shipping applied
        `;
        statusText.style.display = 'flex';
      }
    } else {
      if (statusText) {
        statusText.style.display = 'none';
      }
    }
  }
}

function updateProtectionStatus(cart) {
  const statusContainer = document.querySelector('.cart-subtotal-section');
  if (!statusContainer) return;
  
  // Check if protection is in cart
  const hasProtection = cart.items.some(item => 
    item.handle === 'shipping-protection' || 
    item.product_title.toLowerCase().includes('shipping protection') ||
    item.product_title.toLowerCase().includes('protection')
  );
  
  let protectionText = statusContainer.querySelector('.cart-status-text--protected');
  
  if (hasProtection) {
    if (!protectionText) {
      protectionText = document.createElement('div');
      protectionText.className = 'cart-status-text cart-status-text--protected';
      protectionText.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M11.667 3.5L5.25 9.917 2.333 7" stroke="#10B981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Your order is protected
      `;
      
      // Insert after free shipping text or after subtotal
      const freeShippingText = statusContainer.querySelector('.cart-status-text:not(.cart-status-text--protected)');
      if (freeShippingText) {
        freeShippingText.insertAdjacentElement('afterend', protectionText);
      } else {
        const subtotal = statusContainer.querySelector('.cart-subtotal');
        if (subtotal) {
          subtotal.insertAdjacentElement('afterend', protectionText);
        }
      }
    }
    protectionText.style.display = 'flex';
  } else {
    if (protectionText) {
      protectionText.style.display = 'none';
    }
  }
}

// Upsell Add to Cart - NO PAGE RELOAD, just refresh cart section
window.addUpsellToCart = function(buttonElement, variantId) {
  const button = buttonElement;
  const upsellItem = button.closest('.cart-upsell-item');
  const originalText = button.textContent;
  
  if (!variantId) {
    alert('Product variant not found');
    return;
  }
  
  button.disabled = true;
  button.textContent = 'Adding...';
  
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity: 1 })
  })
  .then(response => {
    if (!response.ok) throw new Error('Failed to add to cart');
    return response.json();
  })
  .then(data => {
    // Show success state
    button.textContent = 'Added!';
    button.style.background = '#10b981';
    button.style.borderColor = '#10b981';
    button.style.color = '#fff';
    
    // Update totals and show next upsell
    updateCartTotals();
    refreshCartItems();
    
    // Remove current upsell and show next after delay
    setTimeout(() => {
      upsellItem.style.opacity = '0';
      upsellItem.style.transform = 'scale(0.95)';
      
      setTimeout(() => {
        const container = document.getElementById('cart-upsells-container');
        const items = Array.from(container.querySelectorAll('.cart-upsell-item'));
        
        // Remove the item
        upsellItem.remove();
        
        // Get remaining items
        const remainingItems = Array.from(container.querySelectorAll('.cart-upsell-item'));
        
        if (remainingItems.length === 0) {
          // No more upsells, remove section
          const upsellsSection = document.querySelector('.cart-upsells');
          if (upsellsSection) upsellsSection.remove();
        } else {
          // Update arrow visibility
          const prevBtn = document.getElementById('upsell-prev');
          const nextBtn = document.getElementById('upsell-next');
          
          if (remainingItems.length === 1) {
            // Only one left - hide arrows
            if (prevBtn) prevBtn.style.display = 'none';
            if (nextBtn) nextBtn.style.display = 'none';
          }
          
          // Show next upsell (keep current index, since we removed one)
          if (currentUpsellIndex >= remainingItems.length) {
            currentUpsellIndex = 0;
          }
          updateUpsellDisplay();
        }
      }, 300);
    }, 800);
  })
  .catch(err => {
    console.error('Add to cart error:', err);
    button.disabled = false;
    button.textContent = originalText;
    button.style.background = '';
    button.style.borderColor = '';
    button.style.color = '';
    alert('Failed to add item. Please try again.');
  });
};

// Refresh cart items without reloading page
function refreshCartItems() {
  fetch('/cart.js')
    .then(res => res.json())
    .then(cart => {
      // Just fetch the cart section HTML and replace it
      fetch(window.location.href)
        .then(res => res.text())
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newCartItems = doc.querySelector('.cart-drawer__items');
          const currentCartItems = document.querySelector('.cart-drawer__items');
          
          if (newCartItems && currentCartItems) {
            currentCartItems.innerHTML = newCartItems.innerHTML;
          }
        });
    })
    .catch(err => console.error('Refresh cart error:', err));
}

// Initialize on load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initCartTimer();
    initUpsellNav();
  });
} else {
  initCartTimer();
  initUpsellNav();
}

// Re-initialize when cart opens
document.addEventListener('cart:drawer:opened', function() {
  setTimeout(() => {
    initCartTimer();
    initUpsellNav();
  }, 300);
});

document.addEventListener('cart:refresh', function() {
  setTimeout(() => {
    initCartTimer();
    initUpsellNav();
  }, 300);
});

// Upsell navigation
let currentUpsellIndex = 0;

function initUpsellNav() {
  const container = document.getElementById('cart-upsells-container');
  const prevBtn = document.getElementById('upsell-prev');
  const nextBtn = document.getElementById('upsell-next');
  
  if (!container) {
    console.log('Upsell container not found');
    return;
  }
  
  const items = Array.from(container.querySelectorAll('.cart-upsell-item'));
  console.log('Found', items.length, 'upsell items');
  
  if (items.length === 0) return;
  
  // Always hide arrows initially
  if (prevBtn) prevBtn.style.display = 'none';
  if (nextBtn) nextBtn.style.display = 'none';
  
  if (items.length === 1) {
    // Only one item - show it and keep arrows hidden
    if (items[0]) items[0].classList.add('active');
    return;
  }
  
  // Multiple items - show arrows
  if (prevBtn) prevBtn.style.display = 'flex';
  if (nextBtn) nextBtn.style.display = 'flex';
  
  currentUpsellIndex = 0;
  updateUpsellDisplay();
}

window.scrollUpsells = function(direction) {
  const container = document.getElementById('cart-upsells-container');
  if (!container) {
    console.log('Container not found');
    return;
  }
  
  const items = Array.from(container.querySelectorAll('.cart-upsell-item'));
  console.log('Scrolling', direction, 'Current index:', currentUpsellIndex, 'Total:', items.length);
  
  if (items.length <= 1) return;
  
  if (direction === 'next') {
    currentUpsellIndex = (currentUpsellIndex + 1) % items.length;
  } else if (direction === 'prev') {
    currentUpsellIndex = (currentUpsellIndex - 1 + items.length) % items.length;
  }
  
  console.log('New index:', currentUpsellIndex);
  updateUpsellDisplay();
};

function updateUpsellDisplay() {
  const container = document.getElementById('cart-upsells-container');
  if (!container) return;
  
  const items = Array.from(container.querySelectorAll('.cart-upsell-item'));
  
  items.forEach((item, index) => {
    if (index === currentUpsellIndex) {
      item.classList.add('active');
    } else {
      item.classList.remove('active');
    }
  });
  
  console.log('Updated display, showing item', currentUpsellIndex);
}